/**
 * Rechte-Matrix für die Hilfe „Meine Rolle & was ich darf" (PV-037, A4). Rein, ohne I/O.
 *
 * Die Tabelle wird NICHT beschrieben, sondern berechnet: Je Fall („Projekt in meinem Segment",
 * „außerhalb", „mein eigenes", „Service-Projekt") entsteht ein synthetisches `ProjectScopeShape`,
 * und jede Zelle fragt `canDoProjectAction` — dieselbe Funktion, die Backend (`assertProjectAccess`)
 * und Knöpfe im Frontend nutzen. Dadurch kann die Tabelle nicht von der Wirklichkeit abweichen.
 */
import {
  type BefundBezug,
  PROJECT_ACTIONS,
  type PermissionUser,
  type ProjectAction,
  type ProjectScopeShape,
  canAssignFunctionRole,
  canDoProjectAction,
  canEnterGatedPhase,
  canManageTagInScope,
  canResolveStaffing,
  canWriteSegmentNotes,
  scopeSegmentsForRole,
} from '../permissions.js';
import { SEGMENT_SCOPED_ROLES, type Segment, SegmentSchema } from '../schemas/index.js';

export type RechteFall =
  /** Projekt in einem Segment meiner Rollen */
  | { art: 'segment'; segment: Segment }
  /** Projekt außerhalb meiner Segmente (ohne eigene Segmente: „beliebiges Projekt") */
  | { art: 'fremd'; segment: Segment }
  /** Ich bin als PL eingetragen — im fremden Segment, damit nur die PL-Eigenschaft wirkt */
  | { art: 'eigen'; segment: Segment }
  /** responsibility = Service in einem Segment meiner Service-Leitung */
  | { art: 'service'; segment: Segment };

export type RechteZeile = {
  aktion: ProjectAction;
  /** Zweite Zeile für `assign_lead_manager`: Projekt steht in der Eingangsphase (#78). */
  eingang: boolean;
  /** Ein Wert je Eintrag in `faelle`, gleiche Reihenfolge. */
  werte: boolean[];
};

export const WEITERE_RECHTE = [
  'staffing_erledigen',
  'eingang_verlassen',
  'segment_notizen',
  'tags_pflegen',
  'rollen_vergeben',
] as const;
export type WeiteresRecht = (typeof WEITERE_RECHTE)[number];

/** Rechte, die an einem Segment hängen (Anzeige mit Segment bzw. „alle Segmente"). */
export const SEGMENT_RECHTE: readonly WeiteresRecht[] = [
  'segment_notizen',
  'tags_pflegen',
  'rollen_vergeben',
];

export type WeiteresRechtEintrag = {
  recht: WeiteresRecht;
  /** Bei Segment-Rechten: `null` + `erlaubt` = in allen Segmenten. Sonst immer `null`. */
  segment: Segment | null;
  erlaubt: boolean;
};

export type RechteMatrix = {
  faelle: RechteFall[];
  zeilen: RechteZeile[];
  weitere: WeiteresRechtEintrag[];
};

/**
 * Keine eigene Zeile: `read` ist immer ja (Einleitungssatz), `staffing_resolve` ist projektlos und
 * steht unter „weitere Rechte" (`canResolveStaffing`).
 */
export const MATRIX_AUSGENOMMEN: readonly ProjectAction[] = ['read', 'staffing_resolve'];

/** Befund-Aktionen: Anzeige mit Fußnote, nur bei Flag `befunde` (entscheidet die Oberfläche). */
export const BEFUND_MATRIX_AKTIONEN: readonly ProjectAction[] = [
  'befund_zurueckstellen',
  'befund_nachhaken',
  'befund_antworten',
];

/** Platzhalter für „jemand anderes" in PL/Manager/MTB des synthetischen Projekts. */
const FREMDE_PERSON = '00000000-0000-4000-8000-000000000000';

/**
 * Befund-Bezug der Tabelle: niemand zuständig, keine Tag-Manager, Zustand offen → zeigt die
 * Aufsicht. Die Zuständigen-Regel steht als Fußnote (Anforderung „Meine Rolle").
 */
const AUFSICHT_BEZUG: BefundBezug = {
  zustaendig_user_ids: [],
  tag_manager_user_ids: [],
  zustand: 'offen',
};

/** Segmente aller eigenen Funktionsrollen, ohne Doppel, in der Reihenfolge von `SegmentSchema`. */
export function eigeneSegmente(user: PermissionUser): Segment[] {
  const menge = new Set<Segment>();
  for (const r of user.function_roles) if (r.scope_segment) menge.add(r.scope_segment);
  return SegmentSchema.options.filter((s) => menge.has(s));
}

export function rechteFaelle(user: PermissionUser): RechteFall[] {
  const eigene = eigeneSegmente(user);
  const faelle: RechteFall[] = eigene.map((segment) => ({ art: 'segment', segment }));
  const fremd = SegmentSchema.options.find((s) => !eigene.includes(s));
  if (fremd) faelle.push({ art: 'fremd', segment: fremd });
  faelle.push({ art: 'eigen', segment: fremd ?? 'KI' });
  const service = new Set(scopeSegmentsForRole(user, 'service_leitung'));
  for (const segment of SegmentSchema.options) {
    if (service.has(segment)) faelle.push({ art: 'service', segment });
  }
  return faelle;
}

function fallProjekt(user: PermissionUser, fall: RechteFall, eingang: boolean): ProjectScopeShape {
  return {
    project_id: 'hilfe-rechte-matrix',
    segment: fall.segment,
    project_lead_user_id: fall.art === 'eigen' ? user.user_id : FREMDE_PERSON,
    manager_user_id: FREMDE_PERSON,
    mtb_user_id: FREMDE_PERSON,
    responsibility: fall.art === 'service' ? 'Service' : 'PU',
    phase_is_intake: eingang,
    // Die Hilfe beschreibt die Rechte an einem normalen (aktiven) Projekt; zusammengeführte
    // Projekte sind für alle nur lesbar (#123).
    lifecycle_status: 'active',
  };
}

/**
 * Wer bei einem Projekt als PL eingetragen ist, bekommt die Rolle `projektleiter` zur Laufzeit
 * dazu (`loadUserById`, F1). Die Spalte „mein eigenes Projekt" rechnet deshalb mit dieser Rolle.
 */
function mitPlRolle(user: PermissionUser): PermissionUser {
  if (user.function_roles.some((r) => r.role === 'projektleiter')) return user;
  return {
    ...user,
    function_roles: [...user.function_roles, { role: 'projektleiter', scope_segment: null }],
  };
}

function zelle(
  user: PermissionUser,
  fall: RechteFall,
  aktion: ProjectAction,
  eingang: boolean,
): boolean {
  const pruefer = fall.art === 'eigen' ? mitPlRolle(user) : user;
  const bezug = BEFUND_MATRIX_AKTIONEN.includes(aktion) ? AUFSICHT_BEZUG : undefined;
  return canDoProjectAction(pruefer, fallProjekt(user, fall, eingang), aktion, bezug);
}

/**
 * Segment-Recht: in allen Segmenten erlaubt → eine Zeile „alle Segmente"; sonst je eigenem
 * Segment eine Zeile; ohne eigenes Segment eine Zeile „nein".
 */
function segmentRecht(
  user: PermissionUser,
  recht: WeiteresRecht,
  erlaubtIn: (segment: Segment) => boolean,
): WeiteresRechtEintrag[] {
  if (SegmentSchema.options.every(erlaubtIn)) return [{ recht, segment: null, erlaubt: true }];
  const eigene = eigeneSegmente(user);
  if (eigene.length === 0) return [{ recht, segment: null, erlaubt: false }];
  return eigene.map((segment) => ({ recht, segment, erlaubt: erlaubtIn(segment) }));
}

export function rechteMatrix(user: PermissionUser): RechteMatrix {
  const faelle = rechteFaelle(user);
  const zeilen: RechteZeile[] = [];
  for (const aktion of PROJECT_ACTIONS) {
    if (MATRIX_AUSGENOMMEN.includes(aktion)) continue;
    zeilen.push({
      aktion,
      eingang: false,
      werte: faelle.map((f) => zelle(user, f, aktion, false)),
    });
    if (aktion === 'assign_lead_manager') {
      zeilen.push({
        aktion,
        eingang: true,
        werte: faelle.map((f) => zelle(user, f, aktion, true)),
      });
    }
  }
  const weitere: WeiteresRechtEintrag[] = [
    { recht: 'staffing_erledigen', segment: null, erlaubt: canResolveStaffing(user) },
    { recht: 'eingang_verlassen', segment: null, erlaubt: canEnterGatedPhase(user) },
    ...segmentRecht(user, 'segment_notizen', (s) => canWriteSegmentNotes(user, s)),
    ...segmentRecht(user, 'tags_pflegen', (s) => canManageTagInScope(user, s)),
    ...segmentRecht(user, 'rollen_vergeben', (s) =>
      SEGMENT_SCOPED_ROLES.some((r) => canAssignFunctionRole(user, r, s)),
    ),
  ];
  return { faelle, zeilen, weitere };
}
